import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  CostReviewDatabaseRecord,
  EstimateData,
  EstimateSaveRequest,
  SQWebProjectContainer
} from '../models/dtos';
import { CommonDataService } from './common-data.service';
import { OpenEstimateApiService } from './api/open-estimate.service';
import { CostReviewRecordBuilderService } from './review/cost-review-record-builder.service';
import { NotificationMessageService } from './common/notification-message.service';
import { GocCollaborationRestrictionHandlerService } from './common/goc-collaboration-restriction-handler.service';
import { EstimateSaveRequestBuilderService } from './estimate-save-request-builder.service';
import { GlobalApiErrorHandlerService } from '../core/errors/global-api-error-handler.service';
import { ProblemDetailsHttpStatus } from '../models/enum';

@Injectable({
  providedIn: 'root'
})
export class EstimateSaveService {

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly openEstimateApiService: OpenEstimateApiService,
    private readonly costReviewRecordBuilderService: CostReviewRecordBuilderService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly estimateSaveRequestBuilderService: EstimateSaveRequestBuilderService,
    private readonly globalApiErrorHandlerService: GlobalApiErrorHandlerService
  ) {}

  async save(): Promise<void> {
    const estimateData = this.commonDataService.getEstimateData();
    if (!estimateData) {
      console.warn('No estimate data available to save.');
      return;
    }
    try {
      const request = await this.createSaveEstimateRequest();
      await firstValueFrom(this.openEstimateApiService.saveProject(request));
      // Committed only after the request succeeds, so a failed save keeps the original status
      // available for auto-unlock to revert to.
      this.costReviewRecordBuilderService.commitAutoUnlock();
    } catch (err) {
      if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(err)) {
        return;
      }

      if (err instanceof HttpErrorResponse && err.status === ProblemDetailsHttpStatus.Forbidden) {
        this.globalApiErrorHandlerService.handle(err);
      } else {
        this.notificationMessageService.showError('SAVE_PROJECT_FAILED');
      }
      throw err;
    }
  }

  async createSaveEstimateRequest(): Promise<EstimateSaveRequest> {
    return this.estimateSaveRequestBuilderService.createSaveEstimateRequest();
  }

  buildEstimateSaveRequest(
    estimateData: EstimateData,
    projectContainer: SQWebProjectContainer,
    costReviewData: CostReviewDatabaseRecord
  ): EstimateSaveRequest {
    return this.estimateSaveRequestBuilderService.buildEstimateSaveRequest(
      estimateData,
      projectContainer,
      costReviewData
    );
  }
}
